import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  IconButton,
  Input,
  InputGroup,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiCopy, FiEye, FiEyeOff, FiGrid, FiRefreshCw, FiUserPlus } from 'react-icons/fi'
import {
  useEnrollmentCode,
  useRotateEnrollmentCode,
  useSetCodeActive,
  useSetEnrollmentCap,
} from '../../../hooks/useCampaign'
import { extractApiMessage } from '../../../lib/apiError'
import { useFormatInstanceCode } from '../../../hooks/useInstanceCode'
import { Card, ResponsiveModal, StatusChip, type StatusVariant } from '../../shared'
import type { EnrollmentCodeState } from '../../../types/canvasserGrant'
import { SharedCodeQrModal } from './SharedCodeQrModal'

// L11 slice 4, Task 10 · [CANV-4]: campaign-wide self-enrollment code card, sibling to
// CanvasserRosterTable/FieldProfileToggle on the Canvassers page. Deliberately usability-framed
// copy (no lock/"arm"/"disarm" language) -- this is a share-a-code-with-canvassers feature, not a
// security control from the organizer's point of view.
//
// [CANV-4] EnrollmentCodeDto.rawCode is now PERSISTENT (stored plaintext, surfaced on every GET),
// not show-once: the inline Code field shows whenever rawCode is present and the code is on, and a
// "QR code" button opens SharedCodeQrModal (encodes {origin}/join/{rawCode}). A pre-migration row
// has rawCode null until its next rotate -- that shows a "rotate once" cue instead of the code
// field, and the QR button is disabled. Rotate is a DELIBERATE invalidation (old code stops
// working; printed signs must be reprinted) -- the confirm copy warns about that, and the old
// show-once "New shared code" modal is gone (redundant now that the code persists in place).

interface SharedCodeCardProps {
  campaignId: string
}

// Off/Active/Full pill idiom (spec: success/info for Active, warning for Full, neutral for Off).
const STATUS_VARIANT: Record<EnrollmentCodeState, StatusVariant> = {
  Active: 'success',
  Full: 'warning',
  Locked: 'warning',
  Off: 'neutral',
}

const MASK = '••••-••••'

export function SharedCodeCard({ campaignId }: SharedCodeCardProps) {
  const { data: code, isLoading } = useEnrollmentCode(campaignId)
  const setActive = useSetCodeActive(campaignId)
  const rotate = useRotateEnrollmentCode(campaignId)
  const setCap = useSetEnrollmentCap(campaignId)
  const formatCode = useFormatInstanceCode() // [Native B1, NB1-11] <orgId>-<CODE>

  const [revealed, setRevealed] = useState(false)
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false)
  const [qrOpen, setQrOpen] = useState(false)
  const [capDraft, setCapDraft] = useState('')

  // Keep the cap draft in sync with the server value whenever it changes underneath us
  // (a fresh load, or another tab's edit landing via refetch) -- but only while the field
  // isn't mid-edit is out of scope for this slice; simplest correct behavior is to reset on
  // every server-value change, matching the field's read-through-then-edit pattern used
  // elsewhere in this feature (e.g. FieldProfileToggle's Switch is always server-driven).
  useEffect(() => {
    if (code) setCapDraft(code.redemptionCap == null ? '' : String(code.redemptionCap))
  }, [code?.redemptionCap])

  if (isLoading || !code) {
    return (
      <Card>
        <HStack gap={3}>
          <Spinner size="sm" color="accent" />
          <Text color="muted">Loading shared code…</Text>
        </HStack>
      </Card>
    )
  }

  const everCreated = code.rawCode != null || code.redemptionCount > 0 || code.armedAt != null
  const primaryLabel = code.state === 'Off' ? (everCreated ? 'Turn on' : 'Create code') : 'Turn off'

  const handleToggle = async () => {
    try {
      await setActive.mutateAsync({ on: code.state === 'Off' })
    } catch (err) {
      toaster.create({ type: 'error', title: extractApiMessage(err, 'Failed to update the shared code.') })
    }
  }

  const handleResume = async () => {
    // Resume is the same setActive mutation with an explicit on:true (a Locked code is armed, so
    // handleToggle would send on:false and turn it OFF). Turning it on clears the pause server-side.
    try {
      await setActive.mutateAsync({ on: true })
    } catch (err) {
      toaster.create({ type: 'error', title: extractApiMessage(err, 'Failed to resume sign-ups.') })
    }
  }

  const handleRotate = async () => {
    try {
      // [CANV-4] The rotated raw now persists in place (the code field + QR update via refetch);
      // no more show-once modal. Just confirm with a toast.
      await rotate.mutateAsync()
      setRotateConfirmOpen(false)
      toaster.create({ type: 'success', title: 'New code generated' })
    } catch (err) {
      toaster.create({ type: 'error', title: extractApiMessage(err, 'Failed to rotate the code.') })
    }
  }

  const handleCapSave = async () => {
    const trimmed = capDraft.trim()
    const cap = trimmed === '' ? null : Number(trimmed)
    if (cap != null && (!Number.isFinite(cap) || cap < 0)) {
      toaster.create({ type: 'error', title: 'Enter a valid cap, or leave it blank for no limit.' })
      return
    }
    try {
      await setCap.mutateAsync({ cap })
      toaster.create({ type: 'success', title: 'Cap updated' })
    } catch (err) {
      toaster.create({ type: 'error', title: extractApiMessage(err, 'Failed to update the cap.') })
    }
  }

  const copyCode = (value: string | null) => {
    if (!value) return
    navigator.clipboard.writeText(formatCode(value))
    toaster.create({ type: 'success', title: 'Code copied' })
  }

  return (
    <Card>
      <VStack align="stretch" gap={4}>
        <HStack justify="space-between" gap={4} flexWrap="wrap">
          <HStack gap={3} align="start">
            <Box color="accent" mt={1}>
              <FiUserPlus />
            </Box>
            <VStack align="start" gap={1}>
              <HStack gap={2}>
                <Text fontWeight="medium">Shared canvasser code</Text>
                <StatusChip variant={STATUS_VARIANT[code.state]}>{code.state}</StatusChip>
              </HStack>
              {code.state === 'Off' && (
                <Text fontSize="sm" color="muted" maxW="480px">
                  Create a code you can share with canvassers so they can add themselves to this
                  campaign. Turn it off anytime — the people who already joined keep their access.
                </Text>
              )}
            </VStack>
          </HStack>
          <Button
            colorPalette={code.state === 'Off' ? 'brand' : undefined}
            variant={code.state === 'Off' ? 'solid' : 'outline'}
            minH="44px"
            _touch={{ minH: '44px' }}
            loading={setActive.isPending}
            onClick={handleToggle}
          >
            {primaryLabel}
          </Button>
        </HStack>

        {code.state === 'Full' && (
          <Box bg="warning.subtle" borderRadius="md" p={3}>
            <Text fontSize="sm" color="warning.fg">
              Code is full — {code.turnedAwayCount} turned away. Raise the cap to let them in.
            </Text>
          </Box>
        )}

        {code.state === 'Locked' && (
          <Box bg="warning.subtle" borderRadius="md" p={3}>
            <VStack align="start" gap={2}>
              <Text fontSize="sm" color="warning.fg">
                Sign-ups paused after a burst of activity
                {code.lockedUntil
                  ? ` — resuming at ${new Date(code.lockedUntil).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                  : ''}
                . Resume now, or rotate the code if it may have leaked.
              </Text>
              {code.lockCount > 0 && (
                <Text fontSize="xs" color="muted">
                  Paused {code.lockCount} times.
                </Text>
              )}
              <Button
                size="sm"
                variant="outline"
                minH="44px"
                _touch={{ minH: '44px' }}
                loading={setActive.isPending}
                onClick={() => void handleResume()}
              >
                Resume now
              </Button>
            </VStack>
          </Box>
        )}

        {code.state !== 'Off' && code.rawCode == null && (
          <Text fontSize="sm" color="muted">
            Rotate once to get a shareable code and QR.
          </Text>
        )}

        {code.rawCode != null && code.state !== 'Off' && (
          <Field.Root>
            <Field.Label>Code</Field.Label>
            <InputGroup
              endElement={
                <HStack gap={1}>
                  <IconButton
                    aria-label={revealed ? 'Hide code' : 'Reveal code'}
                    size="sm"
                    minH="44px"
                    variant="ghost"
                    onClick={() => setRevealed((r) => !r)}
                  >
                    {revealed ? <FiEyeOff /> : <FiEye />}
                  </IconButton>
                  <IconButton
                    aria-label="Copy code"
                    size="sm"
                    minH="44px"
                    variant="ghost"
                    onClick={() => copyCode(code.rawCode)}
                  >
                    <FiCopy />
                  </IconButton>
                </HStack>
              }
            >
              <Input size="sm" value={revealed ? formatCode(code.rawCode) : MASK} readOnly />
            </InputGroup>
          </Field.Root>
        )}

        <HStack justify="space-between" gap={4} flexWrap="wrap" align="end">
          <Field.Root maxW="220px">
            <Field.Label>Cap (blank = no limit)</Field.Label>
            <HStack gap={2}>
              <Input
                size="sm"
                value={capDraft}
                onChange={(e) => setCapDraft(e.target.value)}
                placeholder="No limit"
              />
              <Button
                size="sm"
                minH="44px"
                variant="outline"
                loading={setCap.isPending}
                onClick={handleCapSave}
              >
                Save
              </Button>
            </HStack>
          </Field.Root>

          <HStack gap={2}>
            <Button
              variant="outline"
              size="sm"
              minH="44px"
              disabled={code.rawCode == null || code.state === 'Off'}
              onClick={() => setQrOpen(true)}
            >
              <FiGrid /> QR code
            </Button>
            <Button
              variant="outline"
              size="sm"
              minH="44px"
              onClick={() => setRotateConfirmOpen(true)}
              loading={rotate.isPending}
            >
              <FiRefreshCw /> Rotate code
            </Button>
          </HStack>
        </HStack>

        <Text fontSize="xs" color="muted">
          {/* [CANV-3] live active enrollees, not the monotonic lifetime redemptionCount */}
          {code.activeCount} joined{code.redemptionCap != null ? ` of ${code.redemptionCap}` : ''}
        </Text>
      </VStack>

      <ResponsiveModal isOpen={rotateConfirmOpen} onClose={() => setRotateConfirmOpen(false)} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Rotate the shared code?</Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} />
            </Dialog.CloseTrigger>
            <Dialog.Body>
              <Text fontSize="sm" color="muted">
                The old code stops working right away. Anyone who already joined keeps their access.
              </Text>
              <Text fontSize="sm" color="muted" mt={2}>
                Any printed QR codes or signs using the old code will stop working.
              </Text>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="outline" size="sm" minH="44px" onClick={() => setRotateConfirmOpen(false)}>
                  Cancel
                </Button>
                <Button
                  colorPalette="brand"
                  size="sm"
                  minH="44px"
                  onClick={handleRotate}
                  loading={rotate.isPending}
                >
                  Rotate code
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>

      {code.rawCode != null && (
        <SharedCodeQrModal
          isOpen={qrOpen}
          onClose={() => setQrOpen(false)}
          campaignId={campaignId}
          rawCode={code.rawCode}
        />
      )}
    </Card>
  );
}
